import type { MessageKey } from './i18n';

/**
 * Who is coming, and how that reaches a tariff card.
 *
 * ---------------------------------------------------------------------------
 * TWO VOCABULARIES, ON PURPOSE
 * ---------------------------------------------------------------------------
 *
 * A quote is priced for a `quantities` map whose KEYS are defined per tariff
 * programme: a ported card uses `q0`, a hand-authored one uses `volwassene`, or
 * whatever the operator named its categories. A guest picker cannot speak that
 * language — a visitor understands adults and children, not `q1` — and it must
 * not have to, because the names change whenever an operator renames a row.
 *
 * So the picker speaks CHANNEL ROLES, from §6: the one guest vocabulary the
 * product has committed to being stable, because "a park counts guests however
 * its price list needs; every OTA counts them exactly one way and will not
 * change". The published grid is keyed by the role for the same reason.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE TRANSLATION HAPPENS, AND WHY IT MOVED
 * ---------------------------------------------------------------------------
 *
 * `/api/public/accommodations` now publishes each card's categories with the
 * role each one plays, so `partyForAccommodation` maps a role-keyed party to
 * that card's own keys, per accommodation, at quote time.
 *
 * It did not used to. This file said the mapping "belongs on the server, next
 * to the categories themselves, and until `/api/public/accommodations` publishes
 * it this picker is honest about the party and approximate about what the party
 * costs". The route grew the field; nothing here was changed to read it. The
 * consequence was not the documented under-quote either, because the API had by
 * then also grown a check: every card that declares categories answered 400,
 * and every tile for one fell back to the ask-us-for-a-price label on a working
 * site. Nine of nineteen, until a browser was pointed at it.
 */

export interface PartyCategory {
  /** Sent as a `quantities` key. A channel role, per §6. */
  key: string;
  label: MessageKey;
  /** Shown under the label, where the park's own definition would go. */
  /** A stay needs somebody in it. */
  min: number;
  max: number;
  initial: number;
}

export const PARTY_CATEGORIES: readonly PartyCategory[] = [
  { key: 'adult', label: 'party.adult', min: 1, max: 12, initial: 2 },
  { key: 'child', label: 'party.child', min: 0, max: 12, initial: 0 },
  { key: 'infant', label: 'party.infant', min: 0, max: 6, initial: 0 },
  // `other` rather than `pet`, because `other` is the fourth channel role and
  // the thing a park most often counts under it is a dog. A park that counts
  // something else under `other` gets the right key and a wrong word, which the
  // catalogue can fix; a park that gets `pet` has the wrong key and nothing can.
  { key: 'other', label: 'party.pet', min: 0, max: 4, initial: 0 },
];

/**
 * What a card says about animals, and what the picker does about it.
 *
 * ---------------------------------------------------------------------------
 * REPORTED: "I can now enter multiple dogs" and "for rental no dogs"
 * ---------------------------------------------------------------------------
 *
 * Both are the same fault. `PARTY_CATEGORIES` is a constant, so every card in
 * the park got the same pet counter running to four — on a rental unit that
 * takes no animals at all, and on a pitch that takes exactly one.
 *
 * The park already states this. A category's channel role says whether it is an
 * animal, and `occupancy.pets.max` is the cap the operator sets on the Guests
 * screen — which `fees.ts` has been ENFORCING at pricing time all along. So the
 * picker was not missing a setting; it was ignoring one, and a guest who ticked
 * a second dog got a refusal dressed up as a missing price.
 *
 * ---------------------------------------------------------------------------
 * OPT-IN, WHICH IS THE OPPOSITE OF HOW PEOPLE ARE TREATED
 * ---------------------------------------------------------------------------
 *
 * No declared animal category means NO CONTROL, not an unlimited one. A card
 * that prices per unit — which is every rental unit in this park — says nothing
 * about dogs, and silence has to mean "ask us" rather than "bring one, free".
 *
 * People are deliberately not treated this way. Dropping a category a card
 * cannot price would push a family of 2+2 into entering four adults, and a
 * confident wrong price is worse than an honest "ask us for a price". A dog has
 * no such fallback: there is nothing else for the guest to enter it as.
 *
 * ONE, when nothing says otherwise. A park that declares an animal category and
 * no cap means a dog, not a kennel, and one is the answer that cannot let a
 * booking through that the engine will then refuse.
 */
export const DEFAULT_PET_LIMIT = 1;

export interface CardShape {
  quantities?: ReadonlyArray<{ channel?: string; key: string }>;
  occupancy?: { petMax?: number };
}

/** The most animals any card in scope will take. Zero means: do not ask. */
export function petLimitFor(accommodations: ReadonlyArray<CardShape> | undefined): number {
  if (!accommodations?.length) return 0;
  let limit = 0;
  for (const a of accommodations) {
    const declared = (a.quantities ?? []).some((q) => (q.channel ?? q.key) === 'other');
    if (!declared) continue;
    limit = Math.max(limit, a.occupancy?.petMax ?? DEFAULT_PET_LIMIT);
  }
  return limit;
}

/**
 * The categories to draw, for the cards actually in front of the guest.
 *
 * The largest limit across the cards rather than the smallest, because the
 * picker is one control over a whole search: capping it at the strictest card
 * would stop a guest booking the pitch that does take a dog. A card that cannot
 * take the party still says so, per card, through `partyForAccommodation`.
 */
export function partyCategoriesFor(
  accommodations: ReadonlyArray<CardShape> | undefined,
): readonly PartyCategory[] {
  const pets = petLimitFor(accommodations);
  const out: PartyCategory[] = [];
  for (const c of PARTY_CATEGORIES) {
    if (c.key !== 'other') { out.push(c); continue; }
    if (pets > 0) out.push({ ...c, max: pets });
  }
  return out;
}

/**
 * Drop counts for categories that are no longer on offer.
 *
 * A guest ticks a dog on the camping page, then narrows to a rental unit. The
 * control disappears; the dog must go with it, or the party carries an animal
 * nobody can see into a quote no card can price, and the guest is left reading
 * "ask us" with nothing on screen to explain it.
 */
export function partyWithin(
  party: Party,
  categories: readonly PartyCategory[],
): Party {
  const allowed = new Map(categories.map((c) => [c.key, c]));
  const out: Party = {};
  for (const [key, count] of Object.entries(party)) {
    const cat = allowed.get(key);
    if (!cat) continue;
    out[key] = Math.min(count, cat.max);
  }
  for (const c of categories) if (out[c.key] === undefined) out[c.key] = c.min;
  return out;
}

export type Party = Record<string, number>;

export function initialParty(): Party {
  return Object.fromEntries(PARTY_CATEGORIES.map((c) => [c.key, c.initial]));
}

/** Zero counts are dropped: an absent key and a key at zero mean the same. */
export function partyForApi(party: Party): Party {
  return Object.fromEntries(Object.entries(party).filter(([, n]) => n > 0));
}

/**
 * A role-keyed party, in one card's own quantity keys.
 *
 * Returns null when the card CANNOT price this party — a guest bringing a dog
 * to a card with no category for one. Null rather than a party with the dog
 * quietly dropped: the caller falls back to "ask us for a price", which is true,
 * where a silent drop is a number that is wrong and looks certain.
 *
 * A card that declares no categories at all prices per unit, and gets the party
 * unchanged; the API ignores it rather than refusing it.
 */
export function partyForAccommodation(
  party: Party,
  categories: ReadonlyArray<{
    key: string; channel?: string; billsRoles?: readonly string[];
  }> | undefined,
): Party | null {
  if (!categories || categories.length === 0) return partyForApi(party);

  const out: Party = {};
  for (const [role, count] of Object.entries(party)) {
    if (count <= 0) continue;
    // `channel ?? key`: a card whose keys are already roles needs no mapping,
    // and is the shape the seeded demo park and every OTA feed use.
    // `billsRoles` where the card has one — a category that charges children as
    // guests accepts both, and they land in the same key so the places included
    // in the base are shared between them rather than doubled.
    const match = categories.filter((c) => (
      c.billsRoles ? c.billsRoles.includes(role) : (c.channel ?? c.key) === role
    ));
    if (match.length === 0) return null;
    // The first, where a card splits one role in two — `volwassene` and
    // `senior` are both adults to a channel. A guess, and the only one
    // available: the picker has no control the guest could use to choose
    // between them, and inventing one would put the park's own pricing
    // structure in front of a visitor who came to see a price.
    const key = match[0]!.key;
    out[key] = (out[key] ?? 0) + count;
  }
  return out;
}

export function partySize(party: Party): number {
  /*
    PEOPLE, not animals.

    This summed every category, so ticking the dog turned "2 gasten" into
    "3 gasten" — and a guest reading that reasonably wonders who the third
    person is. `other` is the channel role a park counts pets under, and a pet
    is not a member of the party in the sense this number means.

    It stays a filter over the declared categories rather than a hardcoded
    exclusion of `other`, so a park that later counts something else under the
    same role does not silently start adding it to a headcount.
  */
  return PARTY_CATEGORIES
    .filter((c) => c.key !== 'other')
    .reduce((total, c) => total + (party[c.key] ?? 0), 0);
}

/** `adult:2,child:1` — the deep-link form. Order follows the declaration. */
export function encodeParty(party: Party): string {
  return PARTY_CATEGORIES
    .filter((c) => (party[c.key] ?? 0) > 0)
    .map((c) => `${c.key}:${party[c.key]}`)
    .join(',');
}

/**
 * Read a party back off a URL.
 *
 * Unknown keys are dropped rather than passed through. A link is written by a
 * WordPress plugin and read by a browser, and anything in between that adds a
 * key to it is not something to forward to a pricing engine.
 */
export function decodeParty(encoded: string | null | undefined): Party | null {
  if (!encoded) return null;
  const known = new Map(PARTY_CATEGORIES.map((c) => [c.key, c]));
  const out: Party = {};
  let any = false;
  for (const part of encoded.split(',')) {
    const [key, raw] = part.split(':');
    const cat = known.get((key ?? '').trim());
    if (!cat) continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 0) continue;
    out[cat.key] = Math.min(n, cat.max);
    any = true;
  }
  if (!any) return null;
  for (const c of PARTY_CATEGORIES) if (out[c.key] === undefined) out[c.key] = c.min;
  return out;
}

/** The age bounds a park has set for a channel role, when its cards agree. */
export interface AgeBand { from?: number; until?: number }

/**
 * The age range behind a party category, read off the park's own cards.
 *
 * ---------------------------------------------------------------------------
 * WHY DERIVED AND NOT A FIELD SOMEBODY TYPES
 * ---------------------------------------------------------------------------
 *
 * The picker's subtitles used to be sentences about the mechanism: the age the
 * park counts as adult, whether a category counts towards the pitch, that a
 * baby sleeps in a travel cot. An operator reading them said, correctly, that a
 * guest wants "18+" and "3 to 18".
 *
 * The obvious next step is a free-text subtitle in the panel. It is the wrong
 * one. The park ALREADY states these ages, on every guest category, because
 * they are what decides whether a person is billed as a child — the Guests
 * screen has had `ageFrom` and `ageUntil` all along and the public catalogue
 * has been sending them. A second field holding the same fact is a second field
 * to forget: the operator moves the child age from three to four for pricing,
 * the subtitle still says three, and the site now disagrees with the invoice.
 * That is this codebase's most-repeated bug — the price factor, the enquiry
 * address, the marker table — and it is always the same shape.
 *
 * Derived, it is configurable in exactly the way that was asked for (the panel,
 * today), it needs no translating, and it maps to a channel without extra work
 * because an OTA carries child ages for the same reason the park does.
 *
 * ---------------------------------------------------------------------------
 * UNANIMITY, OR NOTHING
 * ---------------------------------------------------------------------------
 *
 * One party is picked for the whole search, and the cards may disagree — a
 * lodge counting children from three, a pitch from four. Where they disagree
 * there is no honest single answer, so the subtitle is omitted rather than
 * guessed. Silence is not a regression here: the sentences it replaces said
 * nothing a guest could use either.
 */
export function ageBandFor(
  role: string,
  accommodations: ReadonlyArray<{ quantities?: ReadonlyArray<{
    channel?: string; key: string; ageFrom?: number; ageUntil?: number;
  }> }> | undefined,
): AgeBand | null {
  if (!accommodations?.length) return null;
  const bands: AgeBand[] = [];
  for (const a of accommodations) {
    for (const q of a.quantities ?? []) {
      if ((q.channel ?? q.key) !== role) continue;
      if (q.ageFrom === undefined && q.ageUntil === undefined) continue;
      bands.push({
        ...(q.ageFrom === undefined ? {} : { from: q.ageFrom }),
        ...(q.ageUntil === undefined ? {} : { until: q.ageUntil }),
      });
    }
  }
  if (bands.length === 0) return null;
  const first = JSON.stringify(bands[0]);
  return bands.every((b) => JSON.stringify(b) === first) ? bands[0]! : null;
}
